import type { ContentBBox } from '../geometry/contentBBox'

export interface CameraPanInput {
  /** Camera pan (canvas units of the top-left corner) to clamp. */
  readonly panX: number
  readonly panY: number
  readonly zoom: number
  /** Drawn content — `computeContentBBox`. */
  readonly bbox: ContentBBox
  readonly viewportPxW: number
  readonly viewportPxH: number
  /**
   * Physical px of the map's left edge covered by the floating route panel
   * (0 without it). The content's west edge may come out up to the panel's
   * edge — so Heathrow can be seen right of the panel — and no further.
   */
  readonly leftInsetPx: number
  /**
   * Physical px of free space allowed above the content's top edge and
   * below its bottom edge (0 when omitted).
   */
  readonly verticalPaddingPx?: number
}

/**
 * Keeps the camera on the map: no side of the content goes past its screen
 * edge (the left one counts from the route panel; top and bottom may come
 * in by `verticalPaddingPx` of free space), and content smaller than
 * the visible band sits in its middle. Applied to every camera move — drag,
 * pinch, wheel, fling, animation — the way iOS's scroll view and Android's
 * surface already stop at the content.
 */
export function clampCameraPan(input: CameraPanInput): { panX: number; panY: number } {
  const { bbox, zoom } = input
  const viewW = input.viewportPxW / zoom
  const viewH = input.viewportPxH / zoom
  const inset = input.leftInsetPx / zoom
  const pad = (input.verticalPaddingPx ?? 0) / zoom
  return {
    panX: clampAxis(
      input.panX,
      bbox.minX - inset,
      bbox.maxX - viewW,
      bbox.minX - inset - (viewW - inset - (bbox.maxX - bbox.minX)) / 2,
    ),
    panY: clampAxis(
      input.panY,
      bbox.minY - pad,
      bbox.maxY - viewH + pad,
      bbox.minY - (viewH - (bbox.maxY - bbox.minY)) / 2,
    ),
  }
}

/** `pan` within [min, max]; `centred` when the content does not fill the band. */
function clampAxis(pan: number, min: number, max: number, centred: number): number {
  if (min > max) return centred
  return Math.min(max, Math.max(min, pan))
}
